import React, { useState, useEffect } from 'react';
import { StickyNote, Users, Search, MessageSquare, Send, User, ChevronDown, ChevronRight, Clock, Trash2 } from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion, getDoc, addDoc, Timestamp, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { useThemeStore } from '../../stores/themeStore';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Note {
  id: string;
  content: string;
  createdAt: Date;
  author: string;
  userId: string;
  authorName?: string;
  comments?: Comment[];
  sharedWith?: string[];
  color?: string;
}

interface Comment {
  id: string;
  content: string;
  createdAt: Date;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  photoURL?: string;
}

export function SharedNotesPanel() {
  const [sharedNotes, setSharedNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const [newComments, setNewComments] = useState<{[key: string]: string}>({});
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
  const { darkMode } = useThemeStore();

  useEffect(() => {
    loadSharedNotes();
  }, []);

  const loadSharedNotes = async () => {
    if (!auth.currentUser) return;

    setIsLoading(true);
    try {
      // Rechercher les notes partagées avec l'utilisateur actuel
      const q = query(
        collection(db, 'dashboard_notes'),
        where('sharedWith', 'array-contains', auth.currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const loadedNotes: Note[] = [];
      
      // Récupérer les informations des auteurs
      const userIds = new Set<string>();
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        userIds.add(data.userId);
        
        // Ajouter les IDs des auteurs de commentaires
        if (data.comments && Array.isArray(data.comments)) {
          data.comments.forEach(comment => {
            if (comment.authorId) {
              userIds.add(comment.authorId);
            }
          });
        }
      });
      
      // Charger les informations des utilisateurs
      const usersMap = new Map<string, User>();
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            usersMap.set(userId, { 
              id: userId,
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              photoURL: userData.photoURL
            });
          }
        } catch (error) {
          console.error(`Error loading user ${userId}:`, error);
        }
      }
      
      setUsersMap(usersMap);
      
      // Construire les notes avec les informations des auteurs
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const authorData = usersMap.get(data.userId);
        
        loadedNotes.push({
          id: doc.id,
          content: data.content,
          createdAt: data.createdAt.toDate(),
          author: data.author,
          userId: data.userId,
          authorName: authorData ? 
            `${authorData.firstName || ''} ${authorData.lastName || ''}`.trim() || 
            data.author : 
            data.author,
          comments: data.comments ? data.comments.map(c => ({
            ...c,
            createdAt: c.createdAt instanceof Timestamp ? c.createdAt.toDate() : new Date(c.createdAt)
          })) : [],
          sharedWith: data.sharedWith || [],
          color: data.color
        });
      });
      
      // Trier les notes par date (plus récentes en premier)
      loadedNotes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setSharedNotes(loadedNotes);
    } catch (error) {
      console.error('Error loading shared notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComment = async (noteId: string) => {
    if (!newComments[noteId]?.trim() || !auth.currentUser) return;
    
    try {
      const userInfo = getUserInfo(auth.currentUser.uid);
      
      const comment: Comment = {
        id: crypto.randomUUID(),
        content: newComments[noteId].trim(),
        createdAt: new Date(),
        authorId: auth.currentUser.uid,
        authorName: userInfo.name
      };
      
      // Mettre à jour la note dans Firestore
      const noteRef = doc(db, 'dashboard_notes', noteId);
      await updateDoc(noteRef, {
        comments: arrayUnion({
          ...comment,
          createdAt: Timestamp.fromDate(comment.createdAt)
        })
      });
      
      // Mettre à jour l'état local
      setSharedNotes(prev => prev.map(note => {
        if (note.id === noteId) {
          return {
            ...note,
            comments: [...(note.comments || []), comment]
          };
        }
        return note;
      }));
      
      // Réinitialiser le champ de commentaire
      setNewComments(prev => ({
        ...prev,
        [noteId]: ''
      }));
      
      toast.success('Commentaire ajouté');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleRemoveSharedNote = async (noteId: string) => {
    if (!auth.currentUser) return;
    if (!window.confirm('Voulez-vous vraiment supprimer cette note partagée de votre liste ?')) return;

    try {
      // Récupérer la note
      const noteRef = doc(db, 'dashboard_notes', noteId);
      const noteDoc = await getDoc(noteRef);
      
      if (!noteDoc.exists()) {
        toast.error('Note introuvable');
        return;
      }
      
      const data = noteDoc.data();
      const sharedWith = data.sharedWith || [];
      
      // Retirer l'utilisateur actuel de la liste de partage
      const updatedSharedWith = sharedWith.filter((id: string) => id !== auth.currentUser?.uid);
      
      // Mettre à jour la note dans Firestore
      await updateDoc(noteRef, {
        sharedWith: updatedSharedWith
      });
      
      // Mettre à jour l'état local
      setSharedNotes(prev => prev.filter(note => note.id !== noteId));
      
      toast.success('Note retirée de votre liste');
    } catch (error) {
      console.error('Error removing shared note:', error);
      toast.error('Erreur lors de la suppression de la note partagée');
    }
  };

  const toggleNoteExpansion = (noteId: string) => {
    setExpandedNoteId(expandedNoteId === noteId ? null : noteId);
  };

  const getUserInfo = (userId: string) => {
    if (userId === auth.currentUser?.uid) {
      const displayName = auth.currentUser.displayName || 'Utilisateur';
      const nameParts = displayName.split(' ');
      let initials = '';
      
      if (nameParts.length >= 2) {
        initials = `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      } else if (nameParts.length === 1 && nameParts[0].length > 0) {
        initials = nameParts[0][0].toUpperCase();
      } else {
        initials = 'U';
      }
      
      return {
        name: displayName,
        initials: initials,
        photoURL: auth.currentUser.photoURL
      };
    }
    
    const user = usersMap.get(userId);
    if (!user) return { name: 'Utilisateur', initials: 'U', photoURL: null };
    
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                 user.email.split('@')[0];
    
    let initials = '';
    if (user.firstName && user.lastName) {
      initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (name.length > 0) {
      const nameParts = name.split(' ');
      if (nameParts.length >= 2) {
        initials = `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      } else {
        initials = name[0].toUpperCase();
      }
    } else {
      initials = 'U';
    }
    
    return {
      name,
      initials,
      photoURL: user.photoURL
    };
  };

  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: fr });
  };

  const formatTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: fr });
  };

  const getAuthorInitials = (userId: string) => {
    const user = usersMap.get(userId);
    if (!user) return 'U';
    
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                 user.email.split('@')[0];
    
    if (name.length > 0) {
      const nameParts = name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    
    return 'U';
  };

  const filteredNotes = sharedNotes.filter(note => {
    if (!searchTerm) return true;
    return (
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.authorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.author.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            Notes partagées avec moi
            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              {sharedNotes.length}
            </span>
          </h2>
          <div className="relative w-64">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-400'} w-4 h-4`} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-9 pr-4 py-1.5 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg text-sm`}
            />
          </div>
        </div>

        {isLoading ? (
          <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Chargement des notes partagées...
          </div>
        ) : filteredNotes.length > 0 ? (
          <div className="space-y-4">
            {filteredNotes.map((note) => {
              const authorInitials = getAuthorInitials(note.userId);
              
              return (
                <div
                  key={note.id}
                  className="rounded-lg overflow-hidden"
                >
                  <div
                    className="p-4 rounded-lg cursor-pointer relative"
                    style={{ backgroundColor: note.color || '#FEF3C7' }}
                    onClick={() => toggleNoteExpansion(note.id)}
                  >
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSharedNote(note.id);
                        }}
                        className="p-1 hover:bg-white/20 rounded-lg"
                        title="Supprimer cette note partagée"
                      >
                        <Trash2 className="w-4 h-4 text-gray-800" />
                      </button>
                    </div>
                    <div className="flex justify-between items-start mb-2">
                      <p className="whitespace-pre-wrap pr-10 text-gray-800">{note.content}</p>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNoteExpansion(note.id);
                        }}
                        className="ml-2 p-1 hover:bg-black/5 rounded-full"
                      >
                        {expandedNoteId === note.id ? 
                          <ChevronDown className="w-4 h-4 text-gray-700" /> : 
                          <ChevronRight className="w-4 h-4 text-gray-700" />
                        }
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm text-gray-700">
                      <div className="flex items-center">
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        <span>{formatDate(note.createdAt)}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-1 text-xs font-medium">
                          {authorInitials}
                        </div>
                        <span>Partagée par {note.authorName}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-b-lg mt-1 ${expandedNoteId === note.id ? 'block' : 'hidden'}`}>
                    <div className="mb-4">
                      <h4 className={`text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Commentaires ({note.comments?.length || 0})
                      </h4>
                      
                      <div className={`space-y-3 max-h-60 overflow-y-auto ${note.comments?.length ? 'mb-4' : ''}`}>
                        {note.comments?.map((comment) => {
                          const userInfo = getUserInfo(comment.authorId);
                          const isOwnComment = comment.authorId === auth.currentUser?.uid;
                          
                          return (
                            <div 
                              key={comment.id} 
                              className={`flex ${isOwnComment ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex max-w-[80%] ${isOwnComment ? 'flex-row-reverse' : 'flex-row'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                  isOwnComment 
                                    ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                                    : (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                                }`}>
                                  {userInfo.initials}
                                </div>
                                
                                <div className={`mx-2 px-3 py-2 rounded-lg ${
                                  isOwnComment 
                                    ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                                    : (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                                }`}>
                                  <p className="text-sm">{comment.content}</p>
                                  <div className={`text-xs mt-1 ${
                                    darkMode 
                                      ? (isOwnComment ? 'text-blue-400' : 'text-gray-400')
                                      : (isOwnComment ? 'text-blue-500' : 'text-gray-500')
                                  }`}>
                                    <span>{userInfo.name}</span>
                                    <span className="mx-1">•</span>
                                    <span>{formatDate(comment.createdAt)}</span>
                                    <span className="mx-1">•</span>
                                    <span>{formatTime(comment.createdAt)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {!note.comments?.length && (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Aucun commentaire pour le moment
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Ajouter un commentaire..."
                          value={newComments[note.id] || ''}
                          onChange={(e) => setNewComments({
                            ...newComments,
                            [note.id]: e.target.value
                          })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newComments[note.id]?.trim()) {
                              handleAddComment(note.id);
                            }
                          }}
                          className={`flex-1 rounded-l-lg border ${
                            darkMode 
                              ? 'border-gray-600 bg-gray-600 text-white' 
                              : 'border-gray-300 bg-white text-gray-900'
                          } p-2 text-sm`}
                        />
                        <button
                          onClick={() => handleAddComment(note.id)}
                          disabled={!newComments[note.id]?.trim()}
                          className={`p-2 rounded-r-lg ${
                            darkMode 
                              ? 'bg-blue-700 text-white hover:bg-blue-600' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          } disabled:opacity-50`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {searchTerm 
              ? 'Aucune note partagée ne correspond à votre recherche' 
              : 'Aucune note n\'a été partagée avec vous pour le moment'}
          </div>
        )}
      </div>
    </div>
  );
}